import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { AppStateContext } from "../../state/AppProvider";
import { Assistant } from "../../api";
import { FaPlus, FaCog } from "react-icons/fa";
import { ButtonTooltip } from "../ButtonTooltip";
import { LoadingDots } from "../LoadingDots/LoadingDots";
import { getToken } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { checkGroupMembership, getUserId, resolveEntities } from "../../api/api";

interface Props {
    onAssistantSelected: () => void;
    windowWidth: number;
    setIsAssistantDialogOpen: (open: boolean) => void;
    setAssistantDialogTab: (tab: "manage" | "create") => void;
}

export const AssistantSelector = ({ onAssistantSelected, windowWidth, setIsAssistantDialogOpen, setAssistantDialogTab }: Props) => {
    const appStateContext = useContext(AppStateContext);

    const onAssistantClicked = (assist: Assistant) => {
        appStateContext?.dispatch({ type: "UPDATE_ASSISTANT", payload: assist });
        onAssistantSelected();
    };

    const onCreateAssistantClicked = () => {
        setAssistantDialogTab("create");
        setIsAssistantDialogOpen(true);
    };

    const onManageAssistantsClicked = () => {
        setAssistantDialogTab("manage");
        setIsAssistantDialogOpen(true);
    };

    const assistants = appStateContext?.state.assistants || [];
    const regularAssistants = assistants.filter((a: Assistant) => a.type !== "custom");
    const customAssistants = assistants.filter((a: Assistant) => a.type === "custom");

    const anchorRef = useRef<HTMLDivElement>(null);
    const [menuTop, setMenuTop] = useState<number>(56);
    const [menuLeft, setMenuLeft] = useState<number>(0);
    const [menuMaxHeight, setMenuMaxHeight] = useState<number>(0);
    const [isOpen, setIsOpen] = useState(false);
    const closeTimeoutRef = useRef<number | null>(null);

    const [isAdmin, setIsAdmin] = useState(false);
    const { instance: client } = useMsal();

    //checking if current user is a create custom assistant admin
    useEffect(() => {
        const checkAdmin = async () => {
            const token = await getToken(client);
            const isMember = await checkGroupMembership("b5e74d80-ee44-4310-86f6-91bbcfabb371", token?.accessToken);
            console.log("Checked admin status:", isMember);

            setIsAdmin(isMember);
        };
        checkAdmin();
    }, []);

    useLayoutEffect(() => {
        const recalc = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;
            const top = Math.round(rect.bottom);
            const left = Math.round(rect.left + rect.width / 2);
            const vh = window.visualViewport?.height ?? window.innerHeight;
            const maxH = Math.max(120, Math.floor(vh - top));
            setMenuTop(top);
            setMenuLeft(left);
            setMenuMaxHeight(maxH);
        };

        recalc();

        const onResize = () => recalc();
        const onScroll = () => recalc();
        const onTransitionEnd = () => recalc();

        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onScroll, true);
        document.addEventListener("transitionend", onTransitionEnd, true);

        const mo = new MutationObserver(recalc);
        if (document.body) {
            mo.observe(document.body, { attributes: true, subtree: true, attributeFilter: ["class", "style"] });
        }

        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onScroll, true);
            document.removeEventListener("transitionend", onTransitionEnd, true);
            mo.disconnect();
        };
    }, []);

    const handleMouseEnter = () => {
        if (closeTimeoutRef.current) {
            window.clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        closeTimeoutRef.current = window.setTimeout(() => setIsOpen(false), 0);
    };

    return (
        <div className="flyout-container relative" ref={anchorRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div className="flyout-button flex justify-start cursor-pointer h-14 w-auto whitespace-nowrap items-center transition-all ease-linear duration-300">
                <a className="text-sm leading-6 font-bold">
                    {windowWidth >= 976 ? appStateContext?.state.assistant?.title : appStateContext?.state.assistant?.name}
                </a>
                <div className="px-2">
                    <svg
                        stroke="currentColor"
                        fill="none"
                        strokeWidth="0"
                        viewBox="0 0 24 24"
                        className="flyout-chevron-down stroke-eq-green transition-all ease-in-out duration-300 rotate-0"
                        height="1em"
                        width="1em"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                </div>
            </div>

            <div
                className={`${isOpen ? "block" : "hidden"} flyout-menu z-50 md:w-80 w-screen transition-all ease-linear duration-300 bg-eq-white shadow-lg overflow-y-auto overscroll-contain`}
                style={{
                    position: "fixed",
                    left: `${menuLeft}px`, // follow the trigger horizontally
                    transform: "translateX(-50%)",
                    top: `${menuTop}px`,
                    maxHeight: menuMaxHeight ? `${menuMaxHeight}px` : "100dvh",
                    WebkitOverflowScrolling: "touch",
                    paddingBottom: "env(safe-area-inset-bottom, 12px)",
                    borderTop: "1px solid rgba(0,0,0,0.06)"
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="py-2 px-5">
                    <div className="flex flex-col">
                        {regularAssistants.map((assist: Assistant, index: number) => {
                            const isDisabled = assist.available === false;
                            const tooltipText = "Assistant is learning...";
                            return (
                                <div key={`regular-${index}`} className="leading-7 font-normal py-2">
                                    <ButtonTooltip
                                        title={isDisabled ? tooltipText : ""}
                                        placement="right"
                                        disableHoverListener={!isDisabled}
                                        disableFocusListener={!isDisabled}
                                        disableTouchListener={!isDisabled}
                                        className="ButtonTooltip"
                                    >
                                        <span>
                                            <button
                                                className={`
                                                        gap-1 w-fit ease-linear transition-transform duration-300 flex
                                                        text-center items-center text-lg whitespace-nowrap
                                                        ${isDisabled ? "text-gray-400 cursor-default" : "hover:text-eq-green cursor-pointer"}
                                                    `}
                                                onClick={() => {
                                                    if (!isDisabled) {
                                                        onAssistantClicked(assist);
                                                    }
                                                }}
                                                disabled={isDisabled}
                                            >
                                                <span className="text-[0.875rem]">
                                                    <span className={`${!isDisabled ? "hover-underline pb-[3px]" : "pb-[3px]"}`}>{assist.title}</span>
                                                </span>
                                            </button>
                                        </span>
                                    </ButtonTooltip>
                                </div>
                            );
                        })}

                        {customAssistants.length > 0 && (
                            <>
                                {customAssistants.map((assist: Assistant, index: number) => {
                                    const isDisabled = assist.available === false;
                                    const tooltipText = (
                                        <>
                                            Assistant is learning
                                            <LoadingDots />
                                        </>
                                    );

                                    return (
                                        <div key={`custom-${index}`} className="leading-7 font-normal py-2">
                                            <ButtonTooltip
                                                title={isDisabled ? tooltipText : ""}
                                                placement="right"
                                                disableHoverListener={!isDisabled}
                                                disableFocusListener={!isDisabled}
                                                disableTouchListener={!isDisabled}
                                                className="ButtonTooltip"
                                            >
                                                <span>
                                                    <button
                                                        className={`
                                                                    gap-1 w-fit ease-linear transition-transform duration-300 flex
                                                                    text-center items-center text-lg whitespace-nowrap
                                                                    ${isDisabled ? "text-gray-400 cursor-default" : "hover:text-eq-green cursor-pointer"}
                                                            `}
                                                        onClick={() => {
                                                            if (!isDisabled) {
                                                                onAssistantClicked(assist);
                                                            }
                                                        }}
                                                        disabled={isDisabled}
                                                    >
                                                        <span className="text-[0.875rem]">
                                                            <span className={`${!isDisabled ? "hover-underline pb-[3px]" : "pb-[3px]"}`}>{assist.title}</span>
                                                        </span>
                                                    </button>
                                                </span>
                                            </ButtonTooltip>
                                        </div>
                                    );
                                })}
                            </>
                        )}

                        <div
                            style={{
                                backgroundColor: "#28373A",
                                height: "1px",
                                margin: "8px 0"
                            }}
                        />

                        <div className="leading-7 font-normal py-2">
                            <a
                                className="gap-1 w-fit ease-linear transition-transform duration-300 flex cursor-pointer text-center items-center text-lg hover:text-eq-green whitespace-nowrap"
                                onClick={onManageAssistantsClicked}
                            >
                                <FaCog className="w-3 h-3 mr-1" />
                                <span className="text-[0.875rem]">
                                    <span className="hover-underline pb-[3px]">Manage Assistants</span>
                                </span>
                            </a>
                        </div>
                        {isAdmin && (
                            <div className="leading-7 font-normal py-2">
                                <a
                                    className="gap-1 w-fit ease-linear transition-transform duration-300 flex cursor-pointer text-center items-center text-lg hover:text-eq-green whitespace-nowrap"
                                    onClick={onCreateAssistantClicked}
                                >
                                    <FaPlus className="w-3 h-3 mr-1" />
                                    <span className="text-[0.875rem]">
                                        <span className="hover-underline pb-[3px]">Create Custom Assistant</span>
                                    </span>
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
